import React, { useState, useMemo } from "react";
import {
  FunnelIcon,
  ArrowDownTrayIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { ColumnDef } from "@tanstack/react-table";
import Loader from "../common/Loader";
import Table from "../common/Table";
import FilePreview from "../common/FilePreview";
import CreativeReportFilterModal from "../filters/CreativeReportFilterModal";
import EmailReportModal from "../modals/EmailReportModal";
import EditableCreativeReport from "./EditableCreativeReport";
import {
  useCreativeReport,
  useCreativeReportScreensInfinite,
} from "../../hooks/useReports";
import useDebounce from "../../hooks/useDebounce";
import {
  CreativeReportData,
  CreativeScreenPlayInfo,
  ReportFormat,
} from "../../schemas/reportSchema";
import { reportService } from "../../api/services/reportService";
import { toast } from "react-toastify";
import { getFullMediaUrl } from "../../utils/mediaUtils";

const CreativeReport: React.FC = () => {
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState<boolean>(false);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [isEditableReportOpen, setIsEditableReportOpen] = useState<boolean>(false);
  // The editable download flow edits and totals every played screen, so it
  // needs the unpaginated list — fetched only when that flow is opened.
  const [fullReport, setFullReport] = useState<CreativeReportData | null>(null);
  const [isPreparingReport, setIsPreparingReport] = useState<boolean>(false);
  const [reportFormat, setReportFormat] = useState<ReportFormat>("PDF");
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<{
    adGroupId?: string | null;
    creativeId?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    cityId?: string | null;
    regionId?: string | null;
  } | null>(null);

  // Only fetch report when filters are applied (appliedFilters is set)
  const { reportData, isLoading, error } = useCreativeReport(
    appliedFilters ? appliedFilters.creativeId || null : null,
    appliedFilters || undefined
  );

  // Played-screen rows load a page at a time as the user scrolls, so search
  // runs on the server (debounced) instead of over the loaded pages only.
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search);

  const screensFilters = useMemo(
    () => ({
      startDate: appliedFilters?.startDate ?? null,
      endDate: appliedFilters?.endDate ?? null,
      cityId: appliedFilters?.cityId ?? null,
      regionId: appliedFilters?.regionId ?? null,
      search: debouncedSearch || null,
    }),
    [appliedFilters, debouncedSearch]
  );

  const {
    items: screensPlayed,
    totalCount: screensPlayedCount,
    isLoading: isScreensLoading,
    hasNextPage: hasMoreScreens,
    isFetchingNextPage: isFetchingMoreScreens,
    fetchNextPage: fetchMoreScreens,
  } = useCreativeReportScreensInfinite(
    appliedFilters?.creativeId || null,
    screensFilters
  );

  const handleApplyFilter = (data: {
    adGroupId?: string;
    creativeId?: string;
    startDate?: string;
    endDate?: string;
    cityId?: string;
    regionId?: string;
  }) => {
    if (!data.creativeId) return; // Creative is required

    setAppliedFilters({
      adGroupId: data.adGroupId || null,
      creativeId: data.creativeId || null,
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      cityId: data.cityId || null,
      regionId: data.regionId || null,
    });
    // The table unmounts while the new report loads, taking Table's local
    // search box with it — clear the term so it can't outlive the input.
    setSearch("");
    setIsFilterModalOpen(false);
  };

  const handleResetFilter = () => {
    setAppliedFilters(null);
    setSearch("");
    setIsFilterModalOpen(false);
  };

  // Clear filters but keep the modal open (used by Reset button inside modal)
  const handleClearFilterValues = () => {
    setAppliedFilters(null);
  };

  const handleCloseFilter = () => {
    setIsFilterModalOpen(false);
  };

  // Check if any filters are applied
  const hasActiveFilters = useMemo(() => {
    if (!appliedFilters) return false;
    return !!(
      appliedFilters.creativeId ||
      appliedFilters.startDate ||
      appliedFilters.endDate ||
      appliedFilters.cityId ||
      appliedFilters.regionId
    );
  }, [appliedFilters]);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}hr ${minutes}min ${secs}sec`;
    }
    if (minutes > 0) {
      return `${minutes}min ${secs}sec`;
    }
    return `${secs}sec`;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDownloadPDF = async () => {
    if (!reportData || !appliedFilters?.creativeId) {
      toast.error("No data available to download");
      return;
    }

    try {
      toast.info(`Generating ${reportFormat} report...`);

      const downloadResult = await reportService.downloadCreativeReport(
        appliedFilters.creativeId,
        {
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
          cityId: appliedFilters.cityId,
          regionId: appliedFilters.regionId,
          format: reportFormat,
        }
      );

      // Convert base64 to blob
      const byteCharacters = atob(downloadResult.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadResult.mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadResult.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  // Loads the full played-screens list, then opens the editable download flow.
  const openEditableReport = async (format: ReportFormat) => {
    if (!appliedFilters?.creativeId) {
      toast.error("No data available to download");
      return;
    }

    setReportFormat(format);
    setIsPreparingReport(true);
    try {
      const full = await reportService.getCreativeReport(
        appliedFilters.creativeId,
        {
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
          cityId: appliedFilters.cityId,
          regionId: appliedFilters.regionId,
        }
      );
      setFullReport(full);
      setIsEditableReportOpen(true);
    } catch (error) {
      console.error("Error preparing report:", error);
      toast.error("Failed to prepare report");
    } finally {
      setIsPreparingReport(false);
    }
  };

  const closeEditableReport = () => {
    setIsEditableReportOpen(false);
    setFullReport(null);
  };

  const handleSendEmail = async (email: string, format: ReportFormat) => {
    if (!appliedFilters?.creativeId) {
      toast.error("No report data available to send");
      return;
    }

    setIsSendingEmail(true);
    try {
      await reportService.sendCreativeReportEmail(
        email,
        appliedFilters.creativeId,
        {
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
          cityId: appliedFilters.cityId,
          regionId: appliedFilters.regionId,
          format,
        }
      );
      toast.success("Report sent successfully via email");
      setIsEmailModalOpen(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Column definitions for Screens Played table. Sorting is disabled because
  // rows are server-ordered by screen name across all pages — a client-side
  // sort would only reorder the pages loaded so far.
  const screensPlayedColumns: ColumnDef<CreativeScreenPlayInfo>[] = useMemo(
    () => [
      {
        accessorKey: "screenName",
        header: "Screen Name",
        enableSorting: false,
      },
      {
        accessorKey: "restaurantName",
        header: "Restaurant",
        enableSorting: false,
        cell: ({ row }) => row.original.restaurantName || "-",
      },
      {
        accessorKey: "city",
        header: "City",
        enableSorting: false,
        cell: ({ row }) => row.original.city || "-",
      },
      {
        accessorKey: "region",
        header: "Region",
        enableSorting: false,
        cell: ({ row }) => row.original.region || "-",
      },
      {
        accessorKey: "duration",
        header: "Duration",
        enableSorting: false,
        cell: ({ row }) => formatDuration(row.original.duration),
      },
      {
        accessorKey: "numberOfTimesPlayed",
        header: "Times Played",
        enableSorting: false,
      },
    ],
    []
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header with Action Buttons */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Creative Report</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="relative p-2 bg-primary-default text-white rounded hover:bg-primary-light"
            title="Filter"
            onClick={() => setIsFilterModalOpen(true)}
          >
            <FunnelIcon className="h-5 w-5" />
            {hasActiveFilters && (
              <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span>
            )}
          </button>
          <div className="relative">
            <button
              className="p-2 bg-primary-default text-white rounded hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download"
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={
                !reportData || !appliedFilters?.creativeId || isPreparingReport
              }
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
            </button>
            {showDownloadMenu && (
              <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <button
                  className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg text-left"
                  onClick={() => {
                    setShowDownloadMenu(false);
                    openEditableReport("PDF");
                  }}
                >
                  Download PDF
                </button>
                <button
                  className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg text-left"
                  onClick={() => {
                    setShowDownloadMenu(false);
                    openEditableReport("EXCEL");
                  }}
                >
                  Download Excel
                </button>
              </div>
            )}
          </div>
          <button
            className="p-2 bg-primary-default text-white rounded hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed"
            title="Mail"
            onClick={() => setIsEmailModalOpen(true)}
            disabled={!reportData}
          >
            <EnvelopeIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading report: {error.message}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && appliedFilters && (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader size="large" />
        </div>
      )}

      {/* Report Display */}
      {reportData && !isLoading && (
        <>
          {/* Creative Details - 2 Column Layout */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {/* Left Column - Creative Preview (30-40%) */}
              <div className="md:col-span-2">
                {reportData.fileUrl ? (
                  <div className="w-full h-64 md:h-80">
                    <FilePreview
                      name={reportData.creativeName}
                      type={
                        reportData.fileType?.includes("video")
                          ? "video"
                          : "image"
                      }
                      url={getFullMediaUrl(reportData.fileUrl)}
                      className="w-full h-full object-contain border border-gray-200 bg-white rounded-lg"
                    />
                  </div>
                ) : (
                  <div className="w-full h-64 md:h-80 flex items-center justify-center border border-gray-200 bg-gray-100 rounded-lg">
                    <p className="text-gray-500">No preview available</p>
                  </div>
                )}
              </div>

              {/* Right Column - Creative Info (60-70%) */}
              <div className="md:col-span-3 space-y-4">
                {/* Creative Name */}
                <div className="pb-4 border-b border-gray-200">
                  <p className="text-sm font-medium text-gray-500 mb-1">
                    Creative Name
                  </p>
                  <p className="text-xl font-semibold text-gray-900">
                    {reportData.creativeName}
                  </p>
                </div>

                {/* Other Details */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">
                        Start Date
                      </p>
                      <p className="text-base text-gray-900">
                        {reportData.startDate
                          ? new Date(reportData.startDate).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">
                        End Date
                      </p>
                      <p className="text-base text-gray-900">
                        {reportData.endDate
                          ? new Date(reportData.endDate).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">
                        Total No of Screens
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        {reportData.totalScreensPlayed}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">
                        Number of times Ad played
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        {reportData.numberOfTimesPlayed}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">
                        Total screen time of your ads
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatDuration(reportData.totalScreenTime)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">
                        Average Ad repetition frequency
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        {reportData.averageRepetitionFrequency.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Screens Played List */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Screens Played ({screensPlayedCount})
            </h2>
            <Table
              data={screensPlayed}
              columns={screensPlayedColumns}
              searchable={true}
              onSearch={setSearch}
              pagination={false}
              isLoading={isScreensLoading}
              maxHeight="67vh"
              infinite
              hasNextPage={hasMoreScreens}
              isFetchingMore={isFetchingMoreScreens}
              onLoadMore={() => fetchMoreScreens()}
            />
          </div>
        </>
      )}

      {/* Empty State */}
      {!reportData && !isLoading && (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600 text-center">
            Click the Filter button to select an Ad Group and Creative, then
            click "Generate Report" to view the report
          </p>
        </div>
      )}

      {/* Filter Modal */}
      {isFilterModalOpen && (
        <CreativeReportFilterModal
          visible={isFilterModalOpen}
          initialData={{
            adGroupId: appliedFilters?.adGroupId || undefined,
            creativeId: appliedFilters?.creativeId || undefined,
            startDate: appliedFilters?.startDate || undefined,
            endDate: appliedFilters?.endDate || undefined,
            cityId: appliedFilters?.cityId || undefined,
            regionId: appliedFilters?.regionId || undefined,
          }}
          onSubmit={handleApplyFilter}
          onCancel={handleResetFilter}
          onClose={handleCloseFilter}
          onReset={handleClearFilterValues}
        />
      )}

      {/* Email Modal */}
      {isEmailModalOpen && (
        <EmailReportModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          onSubmit={handleSendEmail}
          isLoading={isSendingEmail}
          reportName="Creative Report"
          defaultFormat={reportFormat}
        />
      )}

      {/* Editable Report Modal */}
      {isEditableReportOpen && fullReport && appliedFilters?.creativeId && (
        <EditableCreativeReport
          isOpen={isEditableReportOpen}
          onClose={closeEditableReport}
          reportData={fullReport}
          creativeId={appliedFilters.creativeId}
          filters={{
            startDate: appliedFilters?.startDate,
            endDate: appliedFilters?.endDate,
            cityId: appliedFilters?.cityId,
            regionId: appliedFilters?.regionId,
          }}
          format={reportFormat}
        />
      )}
    </div>
  );
};

export default CreativeReport;
